import * as THREE from 'three';
const TAP_MOVE = 14;
const TAP_TIME = 450;
const DOUBLE_TAP_DISTANCE = 32;
const DOUBLE_TAP_TIME = 450;
const POLAR_EPSILON = 1e-4;
function wrappedAngleDelta(next, previous) {
    let delta = next - previous;
    while (delta > Math.PI) {
        delta -= Math.PI * 2;
    }
    while (delta < -Math.PI) {
        delta += Math.PI * 2;
    }
    return delta;
}
/** Purpose-built touch navigation, intentionally independent of mouse controls. */
export class TouchNavigationController {
    constructor(options) {
        this.pointers = new Map();
        this.inputMode = null;
        this.sessionControls = null;
        this.controlsWereEnabled = true;
        this.worldUp = new THREE.Vector3(0, 1, 0);
        this.lastOne = null;
        this.lastPair = null;
        this.hadMultiple = false;
        this.tap = null;
        this.previousTap = null;
        this.onPointerDown = (event) => {
            if (event.pointerType !== 'touch') {
                return;
            }
            this.claim(event);
            this.inputMode = 'pointer';
            this.pointers.set(event.pointerId, new THREE.Vector2(event.clientX, event.clientY));
            if (this.pointers.size === 1) {
                this.beginSession();
                this.lastOne = this.pointers.get(event.pointerId).clone();
                this.startTap(event.pointerId, event.clientX, event.clientY);
            }
            else {
                this.startPair();
            }
        };
        this.onPointerMove = (event) => {
            if (event.pointerType !== 'touch' || !this.pointers.has(event.pointerId)) {
                return;
            }
            this.claim(event);
            this.pointers.set(event.pointerId, new THREE.Vector2(event.clientX, event.clientY));
            this.updateTap(event.pointerId, event.clientX, event.clientY);
            this.applyGesture();
        };
        this.onPointerUp = (event) => {
            if (event.pointerType !== 'touch' || !this.pointers.has(event.pointerId)) {
                return;
            }
            this.claim(event);
            if (!this.hadMultiple && event.type !== 'pointercancel') {
                this.finishTap(event.pointerId, event.clientX, event.clientY);
            }
            this.pointers.delete(event.pointerId);
            this.afterFingerLift();
        };
        this.onTouchStart = (event) => {
            if (this.inputMode === 'pointer') {
                return;
            }
            event.preventDefault();
            this.inputMode = 'touch';
            this.syncTouches(event.touches);
            if (this.pointers.size === 1) {
                this.beginSession();
                const [id, point] = [...this.pointers.entries()][0];
                this.lastOne = point.clone();
                this.startTap(id, point.x, point.y);
            }
            else {
                this.startPair();
            }
        };
        this.onTouchMove = (event) => {
            if (this.inputMode !== 'touch') {
                return;
            }
            event.preventDefault();
            this.syncTouches(event.touches);
            if (this.pointers.size === 1 && this.tap) {
                const point = this.pointers.get(this.tap.pointerId);
                if (point) {
                    this.updateTap(this.tap.pointerId, point.x, point.y);
                }
            }
            this.applyGesture();
        };
        this.onTouchEnd = (event) => {
            if (this.inputMode !== 'touch') {
                return;
            }
            if (!this.hadMultiple && event.type !== 'touchcancel' && this.tap) {
                this.finishTap(this.tap.pointerId, this.tap.current.x, this.tap.current.y);
            }
            this.syncTouches(event.touches);
            this.afterFingerLift();
        };
        this.camera = options.camera;
        this.element = options.element;
        this.getControls = options.getControls;
        this.onChange = options.onChange;
        this.onDoubleTap = options.onDoubleTap;
        this.element.addEventListener('pointerdown', this.onPointerDown, { capture: true });
        window.addEventListener('pointermove', this.onPointerMove, { capture: true, passive: false });
        window.addEventListener('pointerup', this.onPointerUp, { capture: true });
        window.addEventListener('pointercancel', this.onPointerUp, { capture: true });
        this.element.addEventListener('touchstart', this.onTouchStart, { passive: false });
        this.element.addEventListener('touchmove', this.onTouchMove, { passive: false });
        this.element.addEventListener('touchend', this.onTouchEnd);
        this.element.addEventListener('touchcancel', this.onTouchEnd);
    }
    dispose() {
        this.element.removeEventListener('pointerdown', this.onPointerDown, { capture: true });
        window.removeEventListener('pointermove', this.onPointerMove, { capture: true });
        window.removeEventListener('pointerup', this.onPointerUp, { capture: true });
        window.removeEventListener('pointercancel', this.onPointerUp, { capture: true });
        this.element.removeEventListener('touchstart', this.onTouchStart);
        this.element.removeEventListener('touchmove', this.onTouchMove);
        this.element.removeEventListener('touchend', this.onTouchEnd);
        this.element.removeEventListener('touchcancel', this.onTouchEnd);
        this.endSession();
        this.pointers.clear();
    }
    beginSession() {
        if (this.sessionControls) {
            return;
        }
        const controls = this.getControls();
        this.sessionControls = controls;
        this.controlsWereEnabled = controls.enabled;
        controls.enabled = false;
        this.stopMomentum(controls);
        this.worldUp.copy(this.camera.up);
        if (this.worldUp.lengthSq() === 0) {
            this.worldUp.set(0, 1, 0);
        }
        this.worldUp.normalize();
        this.camera.up.copy(this.worldUp);
        this.hadMultiple = false;
    }
    endSession() {
        if (!this.sessionControls) {
            return;
        }
        this.stopMomentum(this.sessionControls);
        this.sessionControls.enabled = this.controlsWereEnabled;
        this.sessionControls = null;
        this.lastOne = null;
        this.lastPair = null;
        this.hadMultiple = false;
        this.tap = null;
    }
    startPair() {
        this.hadMultiple = true;
        this.tap = null;
        this.previousTap = null;
        this.lastOne = null;
        this.lastPair = this.pair();
    }
    afterFingerLift() {
        if (this.pointers.size === 1) {
            this.lastPair = null;
            this.lastOne = [...this.pointers.values()][0].clone();
        }
        else if (this.pointers.size === 0) {
            this.endSession();
            this.inputMode = null;
        }
    }
    applyGesture() {
        if (this.pointers.size === 1) {
            const point = [...this.pointers.values()][0];
            if (this.lastOne) {
                this.orbit(point.x - this.lastOne.x, point.y - this.lastOne.y);
            }
            this.lastOne = point.clone();
            return;
        }
        if (this.pointers.size < 2) {
            return;
        }
        const next = this.pair();
        const previous = this.lastPair;
        this.lastPair = next;
        if (!previous) {
            return;
        }
        // Two-finger translation deliberately does nothing: yaw/pitch orbit belongs
        // exclusively to one-finger dragging. Pinching zooms and twisting rolls the
        // view, allowing the user to choose a new up direction.
        this.roll(wrappedAngleDelta(next.angle, previous.angle));
        const controls = this.getControls();
        const eye = this.camera.position.clone().sub(controls.target);
        eye.setLength(THREE.MathUtils.clamp(eye.length() * (previous.distance / next.distance), controls.minDistance ?? 0.001, controls.maxDistance ?? 50000));
        this.camera.position.copy(controls.target).add(eye);
        this.camera.lookAt(controls.target);
        this.onChange();
    }
    orbit(dx, dy, notify = true) {
        if (dx === 0 && dy === 0) {
            return;
        }
        const controls = this.getControls();
        const eye = this.camera.position.clone().sub(controls.target);
        if (eye.lengthSq() === 0) {
            return;
        }
        const yUp = new THREE.Vector3(0, 1, 0);
        const toY = new THREE.Quaternion().setFromUnitVectors(this.worldUp, yUp);
        eye.applyQuaternion(toY);
        const spherical = new THREE.Spherical().setFromVector3(eye);
        const height = Math.max(1, this.element.getBoundingClientRect().height);
        spherical.theta -= (2 * Math.PI * dx) / height;
        spherical.phi -= (2 * Math.PI * dy) / height;
        spherical.phi = THREE.MathUtils.clamp(spherical.phi, POLAR_EPSILON, Math.PI - POLAR_EPSILON);
        eye.setFromSpherical(spherical).applyQuaternion(toY.invert());
        this.camera.position.copy(controls.target).add(eye);
        this.camera.up.copy(this.worldUp);
        this.camera.lookAt(controls.target);
        if (notify) {
            this.onChange();
        }
    }
    roll(angle) {
        if (angle === 0) {
            return;
        }
        const controls = this.getControls();
        const viewDirection = controls.target.clone().sub(this.camera.position);
        if (viewDirection.lengthSq() === 0) {
            return;
        }
        viewDirection.normalize();
        this.camera.up.applyAxisAngle(viewDirection, -angle).normalize();
        // If one finger remains after the twist, its stable-up orbit must preserve
        // the newly chosen up direction rather than snapping back to the old one.
        this.worldUp.copy(this.camera.up);
        this.camera.lookAt(controls.target);
    }
    pair() {
        const [first, second] = [...this.pointers.values()];
        return {
            distance: Math.max(1, first.distanceTo(second)),
            angle: Math.atan2(second.y - first.y, second.x - first.x),
        };
    }
    syncTouches(touches) {
        this.pointers.clear();
        for (let i = 0; i < touches.length; i++) {
            const touch = touches.item(i);
            if (touch) {
                this.pointers.set(touch.identifier, new THREE.Vector2(touch.clientX, touch.clientY));
            }
        }
    }
    claim(event) {
        event.preventDefault();
        event.stopImmediatePropagation();
    }
    startTap(pointerId, x, y) {
        const point = new THREE.Vector2(x, y);
        this.tap = { pointerId, start: point.clone(), current: point, startedAt: performance.now() };
    }
    updateTap(pointerId, x, y) {
        if (!this.tap || this.tap.pointerId !== pointerId) {
            return;
        }
        this.tap.current.set(x, y);
        if (this.tap.current.distanceTo(this.tap.start) > TAP_MOVE) {
            this.tap = null;
        }
    }
    finishTap(pointerId, x, y) {
        const tap = this.tap;
        this.tap = null;
        if (!tap || tap.pointerId !== pointerId) {
            return;
        }
        const now = performance.now();
        const position = new THREE.Vector2(x, y);
        if (now - tap.startedAt > TAP_TIME || position.distanceTo(tap.start) > TAP_MOVE) {
            return;
        }
        if (this.previousTap &&
            now - this.previousTap.completedAt <= DOUBLE_TAP_TIME &&
            position.distanceTo(this.previousTap.position) <= DOUBLE_TAP_DISTANCE) {
            this.previousTap = null;
            this.onDoubleTap(x, y);
        }
        else {
            this.previousTap = { position, completedAt: now };
        }
    }
    stopMomentum(controls) {
        const native = controls;
        if (native._panStart && native._panEnd) {
            native._panStart.copy(native._panEnd);
        }
        if (native._zoomStart && native._zoomEnd) {
            native._zoomStart.copy(native._zoomEnd);
        }
        if (native._lastAngle !== undefined) {
            native._lastAngle = 0;
        }
    }
}
